<script setup lang="ts">
import { computed, ref, watch } from "vue";
import User from "../../../types/auth/User";
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import Popover from "@/components/Reusables/Popover/Popover.vue";

const props = defineProps<{
  users: Array<User>;
  loaded: boolean;
}>();

const emits = defineEmits<{
  /** `undefined` retracts an earlier pick — see `selectedUser` below. */
  selectedUser: [user: User | undefined];
}>();

const INPUT_ID = "user-input";
const LISTBOX_ID = "user-search-listbox";

const searchString = ref<string>("");
const activeIndex = ref<number>(0);

// The pick this field currently stands behind. Picking only ever emitted, so someone who selected a
// user and then cleared the text left the host holding a selection the field no longer shows — and,
// now that the host's Invite button is driven by that selection, enabled over an empty field.
const selectedUser = ref<User>();

// Derived rather than a snapshot: the previous version copied `props.users` into a ref once during
// setup and only refreshed it when the search text changed, so a user list arriving later went
// unseen — the parent papered over that with :key="users.length" to force a remount. A computed
// can't go stale.
const options = computed(() => {
  const query = searchString.value.trim().toLowerCase();
  if (!query) return props.users;
  return props.users.filter((usr) => usr.name.toLowerCase().includes(query));
});

// The active row must stay in range whenever the filter changes. It previously kept whatever index
// the arrow keys had reached, so narrowing the list left it pointing past the end: nothing appeared
// highlighted and Enter silently did nothing (the out-of-range lookup was swallowed by a null guard).
watch(options, () => {
  activeIndex.value = 0;
});

const optionDomId = (index: number) => `user-option-${index}`;
const activeOptionId = computed(() => (options.value.length ? optionDomId(activeIndex.value) : undefined));

const moveActive = (delta: number, open: () => void) => {
  open();
  if (!options.value.length) return;
  activeIndex.value = Math.min(Math.max(activeIndex.value + delta, 0), options.value.length - 1);
  // Scroll on every move. The old version only did this when the index happened to be divisible by
  // four, so arrowing through a filtered list walked the highlight out of the visible area.
  requestAnimationFrame(() => {
    document.getElementById(optionDomId(activeIndex.value))?.scrollIntoView({ block: "nearest" });
  });
};

const select = (option: User | undefined, close: () => void) => {
  if (!option) return;
  // Before the text, so the watcher below sees a selection whose name already matches and doesn't
  // read the assignment as an edit.
  selectedUser.value = option;
  searchString.value = option.name;
  emits("selectedUser", option);
  close();
};

// Any typing that moves the text away from the picked user's name retracts the pick. Retyping the
// exact name is not a re-selection: a user is identified by their id here, not by a display name
// that two people can share.
watch(searchString, (text) => {
  if (!selectedUser.value || text === selectedUser.value.name) return;
  selectedUser.value = undefined;
  emits("selectedUser", undefined);
});

// Escape needs to dismiss the list without also closing the surrounding modal. Modal.vue closes on
// @keyup.esc (not keydown), so stopping the keydown isn't enough — the keyup arrives as its own
// event afterwards and still reaches the modal. So: consume the keydown to close the list, then
// swallow exactly the one keyup that pairs with it. With no list open, Escape is left alone so it
// still closes the modal as usual.
const swallowEscapeKeyup = ref(false);

const onEscapeKeydown = (isOpen: boolean, close: () => void) => {
  if (!isOpen) return;
  swallowEscapeKeyup.value = true;
  close();
};

const onEscapeKeyup = (event: KeyboardEvent) => {
  if (!swallowEscapeKeyup.value) return;
  swallowEscapeKeyup.value = false;
  event.stopPropagation();
};
</script>

<template>
  <!-- Teleported and pinned to the input's width so the list isn't clipped by the modal body's
       overflow-auto and lines up with the field. panel-z is raised because Modal.vue's backdrop sits
       at z-modal, one tier above the popover default, so the panel would otherwise render behind it. -->
  <Popover
    placement="bottom-start"
    :teleport="true"
    :match-trigger-width="true"
    :return-focus="false"
    panel-z="z-tooltip"
    panel-class="max-h-64 overflow-y-auto p-1"
    aria-label="Matching users">
    <template #trigger="{ open, close, isOpen }">
      <div class="flex w-full flex-col">
        <!-- Same mb-1 wrapper SelectInputNew puts around its own InputLabel. Without it this label
             sat flush against its input (0px) while the neighbouring Role label had 4px, so the two
             fields in the invite row didn't line up. -->
        <div class="mb-1 flex items-center">
          <InputLabel
            :id="INPUT_ID"
            label="User"
            :required="true" />
        </div>
        <div class="relative w-full">
          <!-- Full combobox semantics: the old input had none at all, so a screen reader announced a
               plain text field with no hint that a list existed, which option was active, or that
               anything expanded. -->
          <input
            :id="INPUT_ID"
            v-model="searchString"
            type="text"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="isOpen"
            :aria-controls="LISTBOX_ID"
            :aria-activedescendant="isOpen ? activeOptionId : undefined"
            autocomplete="off"
            :disabled="!loaded"
            :placeholder="loaded ? 'Search and select a user' : ''"
            class="focus-input bg-slate-0 rounded-8 disabled:bg-slate-0 h-9 w-full border p-2 px-4 leading-none"
            data-cy="user-select"
            @focus="open"
            @input="open"
            @keydown.down.prevent="moveActive(1, open)"
            @keydown.up.prevent="moveActive(-1, open)"
            @keydown.enter.prevent="select(options[activeIndex], close)"
            @keydown.escape="onEscapeKeydown(isOpen, close)"
            @keyup.escape="onEscapeKeyup"
            @focusout="close" />
          <LoadingSpinner
            v-if="!loaded"
            size="w-6 h-6 absolute right-2 top-2"
            fill="primary" />
        </div>
      </div>
    </template>

    <template #default="{ close }">
      <div
        :id="LISTBOX_ID"
        role="listbox"
        aria-label="Matching users">
        <p
          v-if="!options.length"
          class="t-xs text-type-muted px-3 py-2">
          No users found
        </p>
        <!-- mousedown.prevent keeps focus on the input so picking with the mouse doesn't blur it
             first. That blur is what the old version was working around with a 200ms setTimeout
             before hiding the list. -->
        <div
          v-for="(option, index) in options"
          :id="optionDomId(index)"
          :key="option.id"
          role="option"
          :aria-selected="index === activeIndex"
          data-cy="user-option"
          class="rounded-4 t-small flex w-full cursor-pointer items-center gap-4 p-2"
          :class="{ 'bg-slate-100': index === activeIndex }"
          @mouseenter="activeIndex = index"
          @mousedown.prevent
          @click="select(option, close)">
          <div class="h-8 w-8 shrink-0">
            <img
              :src="option.profilePictureUrl"
              class="h-full w-full rounded-full object-cover"
              :alt="option.name" />
          </div>
          <span class="truncate">{{ option.name }}</span>
        </div>
      </div>
    </template>
  </Popover>
</template>
